import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { mockData } from '../mock';
import { usePageTracking, trackFormSubmit, trackVideoPlay, trackSignupStart } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const location = useLocation();
  
  // Track page view
  usePageTracking('/romania');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const videoRef = useRef(null);
  const [signupFormData, setSignupFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });

  // Scroll to form if navigated from Start page
  useEffect(() => {
    if (location.state?.scrollToForm) {
      setTimeout(() => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  // Auto-play video when modal opens
  useEffect(() => {
    if (showVideoModal && videoRef.current) {
      videoRef.current.play();
    }
  }, [showVideoModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, agreedToPrivacy: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreedToPrivacy) {
      toast.error('Trebuie să acceptați politica de confidențialitate.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contacts/`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        agreed_to_privacy: formData.agreedToPrivacy,
        source: 'romania'
      });

      // Track form submission
      trackFormSubmit('/romania', 'contact');
      
      toast.success('Mulțumim! Formularul a fost trimis cu succes. Vă vom contacta în curând.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error.response?.data?.detail || 'A apărut o eroare. Vă rugăm să încercați din nou.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookCall = () => {
    window.open('https://calendly.com/electrumprosperitygroup/epg-opportunity-call-romania', '_blank');
  };

  const handleJoinNow = () => {
    // Show the signup modal instead of going directly to the link
    setShowSignupModal(true);
  };

  const handleSignupInputChange = (e) => {
    const { name, value } = e.target;
    setSignupFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSignupCheckboxChange = (checked) => {
    setSignupFormData(prev => ({ ...prev, agreedToPrivacy: checked }));
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupFormData.agreedToPrivacy) {
      toast.error('Trebuie să acceptați politica de confidențialitate.');
      return;
    }

    if (!signupFormData.name || !signupFormData.email) {
      toast.error('Vă rugăm să completați numele și emailul.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit contact form first
      await axios.post(`${API}/contacts/`, {
        name: signupFormData.name,
        email: signupFormData.email,
        phone: signupFormData.phone,
        agreed_to_privacy: signupFormData.agreedToPrivacy,
        source: 'romania-signup'
      });

      // Track signup form submission
      trackFormSubmit('/romania', 'signup');
      trackSignupStart('/romania');

      // Fetch the next link in rotation
      const response = await axios.get(`${API}/join-links/next-rotation`);
      const joinLink = response.data.join_now_link;
      
      toast.success('Mulțumim! Veți fi redirecționat către pagina de înscriere.');
      
      // Close modal and reset form
      setShowSignupModal(false);
      setSignupFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });

      // Redirect to the 7K signup page after a short delay
      setTimeout(() => {
        window.open(joinLink, '_blank');
      }, 500);

    } catch (error) {
      console.error('Signup form error:', error);
      toast.error('A apărut o eroare. Vă rugăm să încercați din nou.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="/epg_logo.png" alt="EPG Logo" className="logo-icon" />
            <span className="logo-text">{mockData.header.logoText}</span>
          </div>
          <div className="header-buttons">
            <Button variant="outline" className="header-btn" onClick={handleBookCall}>
              Book a Call
            </Button>
            <Button variant="outline" className="header-btn" onClick={() => window.open('/privacy-ro', '_blank')}>
              Privacy
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">{mockData.hero.badge}</div>
            <h1 className="hero-headline">{mockData.hero.headline}</h1>
            <p className="hero-subheading">{mockData.hero.subheading}</p>
            
            <div className="benefits-list">
              {mockData.hero.benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <Check className="check-icon" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="stats-grid">
              {mockData.hero.stats.map((stat, index) => (
                <Card key={index} className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                    {stat.sublabel && <div className="stat-sublabel">{stat.sublabel}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="hero-ctas">
              <Button className="cta-primary" onClick={handleBookCall}>
                {mockData.hero.ctas[0].text}
              </Button>
              <Button className="cta-secondary" onClick={handleJoinNow}>
                {mockData.hero.ctas[1].text}
              </Button>
            </div>

            <div className="hero-email">
              Email: <a href={`mailto:${mockData.hero.email}`}>{mockData.hero.email}</a>
            </div>
          </div>

          <div className="hero-video">
            <div 
              className="video-thumbnail-container"
              onClick={() => {
                setShowVideoModal(true);
                trackVideoPlay('/romania', 'hero_video');
              }}
              style={{ cursor: 'pointer' }}
            >
              <img 
                src="/dd_clip_thumbnail.jpg" 
                alt="Video Thumbnail"
                className="video-thumbnail-img"
                onError={(e) => { e.target.src = '/epg_logo.png'; }}
              />
              <div className="video-play-overlay">
                <div className="play-button-circle-small">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#000">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Videos Section */}
      <section className="main-videos-section">
        <div className="main-videos-container">
          <div className="video-item">
            <video 
              controls 
              playsInline
              webkit-playsinline="true"
              className="video-player-large" 
              poster="https://customer-assets.emergentagent.com/job_epg-member-portal/artifacts/edq9zd2y_image.png"
            >
              <source src="https://customer-assets.emergentagent.com/job_epg-member-portal/artifacts/n6luwb3b_Wealth%20strategies%20%20RO%20VO%20.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <p className="video-title-large">Strategii de creștere a profitului</p>
          </div>
          <div className="video-item">
            <video 
              controls 
              playsInline
              webkit-playsinline="true"
              className="video-player-large" 
              poster="https://customer-assets.emergentagent.com/job_epg-member-portal/artifacts/aszl405j_image.png"
            >
              <source src="https://customer-assets.emergentagent.com/job_epg-member-portal/artifacts/blvldpri_Accelerating%20your%20wealth%20RO%20VO.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <p className="video-title-large">Accelerarea creșterii profitului</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          {mockData.features.map((feature, index) => (
            <Card key={index} className="feature-card">
              <CardContent className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                {index === 2 && (
                  <Button className="feature-btn" onClick={handleBookCall}>
                    Programează o întâlnire
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-section" id="contact-form">
        <div className="contact-container">
          <h2 className="contact-title">{mockData.contactForm.title}</h2>
          <form onSubmit={handleSubmit} className="contact-form">
            <Input
              type="text"
              name="name"
              placeholder={mockData.contactForm.fields[0].placeholder}
              value={formData.name}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="email"
              name="email"
              placeholder={mockData.contactForm.fields[1].placeholder}
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="tel"
              name="phone"
              placeholder={mockData.contactForm.fields[2].placeholder}
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Button type="submit" className="form-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Se trimite...' : mockData.contactForm.submitText}
            </Button>
            <div className="privacy-checkbox">
              <Checkbox
                id="privacy"
                checked={formData.agreedToPrivacy}
                onCheckedChange={handleCheckboxChange}
                disabled={isSubmitting}
              />
              <label htmlFor="privacy" className="privacy-label">
                {mockData.contactForm.privacyText}{' '}
                <a href="/privacy-ro" target="_blank" rel="noopener noreferrer" className="privacy-link">{mockData.contactForm.privacyLink}</a>.
              </label>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-cta">
            <span>{mockData.footer.cta}</span>
            <Button className="footer-btn" onClick={handleBookCall}>
              Rezervă un apel
            </Button>
            <Button className="footer-btn-secondary" onClick={handleJoinNow}>
              Înscrie-te acum!
            </Button>
          </div>
          <div className="footer-info">
            <span>{mockData.footer.copyright}</span>
            <span>•</span>
            <a href={`mailto:${mockData.footer.email}`}>{mockData.footer.email}</a>
          </div>
        </div>
      </footer>

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="signup-modal-overlay" onClick={() => setShowSignupModal(false)}>
          <div className="signup-modal" onClick={(e) => e.stopPropagation()}>
            <button className="signup-modal-close" onClick={() => setShowSignupModal(false)}>
              <X size={24} />
            </button>
            <h2 className="signup-modal-title">Înregistrare</h2>
            <p className="signup-modal-subtitle">Completați formularul pentru a continua la pagina de înscriere</p>
            
            <form onSubmit={handleSignupSubmit} className="signup-modal-form">
              <div className="signup-form-field">
                <Input
                  type="text"
                  name="signup_name"
                  id="signup_name"
                  placeholder="Nume complet *"
                  value={signupFormData.name}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="email"
                  name="signup_email"
                  id="signup_email"
                  placeholder="Email *"
                  value={signupFormData.email}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="tel"
                  name="signup_phone"
                  id="signup_phone"
                  placeholder="Telefon"
                  value={signupFormData.phone}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="signup-input"
                />
              </div>
              <div className="signup-checkbox-container">
                <Checkbox
                  id="signup-privacy"
                  checked={signupFormData.agreedToPrivacy}
                  onCheckedChange={handleSignupCheckboxChange}
                />
                <label htmlFor="signup-privacy" className="signup-checkbox-label">
                  Accept <a href="/privacy-ro" target="_blank" rel="noopener noreferrer">Politica de Confidențialitate</a>
                </label>
              </div>
              <Button 
                type="submit" 
                className="signup-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Se procesează...' : 'Continuă'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="video-modal-overlay"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="video-modal-close"
              onClick={() => setShowVideoModal(false)}
            >
              <X size={24} />
            </button>
            <video 
              controls 
              autoPlay
              playsInline
              preload="auto"
              className="video-modal-player"
            >
              <source src="/dd_clip_epg_compressed.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
